import { useCallback, useEffect, useState } from 'react';
import { Download, Eye, CheckCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import {
  openTeacherInvoiceDocument,
  teacherInvoicesApi,
  type TeacherInvoice,
  type TeacherInvoiceDetail as InvoiceDetail,
} from '@/lib/api/teacherInvoices';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const statusLabels: Record<string, string> = {
  entwurf: 'Entwurf',
  versendet: 'Versendet',
  bezahlt: 'Bezahlt',
};

const statusVariant: Record<string, 'warning' | 'info' | 'success'> = {
  entwurf: 'warning',
  versendet: 'info',
  bezahlt: 'success',
};

function formatCents(cents: number): string {
  return (cents / 100).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });
}

function formatDate(d: string): string {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('de-DE');
}

interface Props {
  lehrerId?: number;
  wpUserId?: number;
}

export default function TeacherInvoicesTab({ lehrerId, wpUserId }: Props) {
  const [invoices, setInvoices] = useState<TeacherInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<InvoiceDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async () => {
    if (!lehrerId && !wpUserId) return;
    setLoading(true);
    try {
      const data = await teacherInvoicesApi.list({
        ...(lehrerId ? { lehrerId } : { wpUserId }),
        limit: 50,
      });
      setInvoices(Array.isArray(data?.items) ? data.items : []);
    } catch {
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  }, [lehrerId, wpUserId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkPaid = async (inv: TeacherInvoice) => {
    try {
      await teacherInvoicesApi.updateStatus(inv.id, 'bezahlt');
      toast.success(`Rechnung ${inv.invoiceNumber} als bezahlt markiert`);
      setInvoices((prev) =>
        prev.map((i) =>
          i.id === inv.id ? { ...i, status: 'bezahlt' as const } : i
        )
      );
    } catch {
      toast.error('Fehler beim Aktualisieren des Status');
    }
  };

  const handleDownload = async (inv: TeacherInvoice) => {
    try {
      const data = await teacherInvoicesApi.downloadUrl(inv.id);
      if (data.url) {
        openTeacherInvoiceDocument(data.url);
      } else {
        toast.error('Kein PDF verfügbar');
      }
    } catch {
      toast.error('Fehler beim Abrufen der Download-URL');
    }
  };

  const handleShowDetail = async (inv: TeacherInvoice) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await teacherInvoicesApi.get(inv.id);
      setDetail({
        ...data,
        positions: Array.isArray(data?.positions) ? data.positions : [],
      });
    } catch {
      toast.error('Fehler beim Laden der Details');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && invoices.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {invoices.length} Rechnung{invoices.length !== 1 ? 'en' : ''}
        </p>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`mr-1 h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {invoices.length === 0 ? (
        <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
          Keine Rechnungen vorhanden
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Rechnungsnr.</TableHead>
                <TableHead>Zeitraum</TableHead>
                <TableHead className="text-right">Betrag</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map((inv) => (
                <TableRow key={inv.id}>
                  <TableCell className="font-mono text-sm">
                    {inv.invoiceNumber}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(inv.periodFrom)} – {formatDate(inv.periodTo)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCents(inv.totalCents)}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant[inv.status] ?? 'secondary'} size="sm">
                      {statusLabels[inv.status] ?? inv.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatDate(inv.invoiceDate)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        title="Details anzeigen"
                        onClick={() => handleShowDetail(inv)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        title="PDF herunterladen"
                        onClick={() => handleDownload(inv)}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      {inv.status === 'versendet' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Als bezahlt markieren"
                          onClick={() => handleMarkPaid(inv)}
                        >
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Rechnung {detail?.invoiceNumber ?? ''}
            </DialogTitle>
            <DialogDescription>
              {detail ? `${formatDate(detail.periodFrom)} bis ${formatDate(detail.periodTo)}` : ''}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="flex h-32 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
            </div>
          ) : detail ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Status:</span>{' '}
                  <Badge variant={statusVariant[detail.status] ?? 'secondary'} size="sm">
                    {statusLabels[detail.status] ?? detail.status}
                  </Badge>
                </div>
                <div>
                  <span className="text-muted-foreground">Betrag:</span>{' '}
                  <span className="font-semibold">{formatCents(detail.totalCents)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Rechnungsdatum:</span>{' '}
                  {formatDate(detail.invoiceDate)}
                </div>
                <div>
                  <span className="text-muted-foreground">Fällig am:</span>{' '}
                  {formatDate(detail.dueDate)}
                </div>
              </div>

              <div>
                <h4 className="mb-2 font-medium">
                  Positionen ({detail.positions.length})
                </h4>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Beschreibung</TableHead>
                        <TableHead>Datum</TableHead>
                        <TableHead>Zeit</TableHead>
                        <TableHead className="text-right">Betrag</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {detail.positions.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell className="text-sm">{p.title}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {formatDate(p.date)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {p.startTime && p.endTime
                              ? `${p.startTime.slice(0, 5)} – ${p.endTime.slice(0, 5)}`
                              : '-'}
                          </TableCell>
                          <TableCell className="text-right text-sm font-medium">
                            {formatCents(p.amountCents)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleDownload(detail)}
                >
                  <Download className="mr-2 h-4 w-4" />
                  PDF herunterladen
                </Button>
                {detail.status === 'versendet' && (
                  <Button
                    onClick={() => {
                      handleMarkPaid(detail);
                      setDetail((d) =>
                        d ? { ...d, status: 'bezahlt' as const } : d
                      );
                    }}
                  >
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Als bezahlt markieren
                  </Button>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
